import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchPermissionList } from '#/api/system/permission';
import {
  createRole,
  fetchRoleDetail,
  fetchRolePermissions,
  replaceRolePermissions,
  updateRole,
} from '#/api/system/role';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import RolePermissionEditor from '#/modules/infra/settings/access/components/RolePermissionEditor';
import {
  ROLE_FIELD_LABELS,
  roleCreateSchema,
  roleEditSchema,
} from '#/modules/infra/settings/access/schemas/roleSchema';

const ROLE_LIST_QUERY_KEY_PREFIX = () => ['system', 'role', 'list'];
const roleDetailQueryKey = (id) => ['system', 'role', 'detail', id];
const rolePermissionsQueryKey = (id) => ['system', 'role', 'permissions', id];

const EMPTY_VALUES = { code: '', name: '', remark: '', permissionIds: [] };

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function isVersionConflict(error) {
  return error?.response?.status === 409 && error?.response?.data?.field === 'updated_at';
}

function readUpdatedAt(result) {
  return result?.data?.record?.updatedAt ?? result?.data?.updatedAt ?? null;
}

const CreateEditRolePage = () => {
  const { id } = useParams();
  const canCreate = useCan(PERM.SYS_ROLE_CREATE);
  const canUpdate = useCan(PERM.SYS_ROLE_UPDATE);
  const queryClient = useQueryClient();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: roleDetailQueryKey(id),
    queryFn: () => fetchRoleDetail(id),
    enabled: Boolean(id),
  });

  const rolePermQuery = useQuery({
    queryKey: rolePermissionsQueryKey(id),
    queryFn: () => fetchRolePermissions(id),
    enabled: Boolean(id),
  });

  const catalogQuery = useQuery({
    queryKey: ['system', 'permission', 'catalog'],
    queryFn: () => fetchPermissionList({
      page: 1,
      pageSize: 500,
      keyword: '',
      sort: [{ id: 'code', desc: false }],
      filters: {},
    }),
  });

  const catalog = useMemo(() => catalogQuery.data?.data ?? [], [catalogQuery.data?.data]);

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    mutationFn: async (values) => {
      const name = values.name.trim();
      const remark = values.remark?.trim() || null;
      // PATCH 與 PUT permissions 共用角色版本：PUT 要用 PATCH 回傳的新 updated_at
      if (id) {
        const updated = await updateRole(id, { name, remark, updatedAt: updatedAtRef.current });
        const versionAfterPatch = readUpdatedAt(updated) ?? updatedAtRef.current;
        const replaced = await replaceRolePermissions(id, values.permissionIds, versionAfterPatch);
        updatedAtRef.current = readUpdatedAt(replaced) ?? versionAfterPatch;
        await queryClient.invalidateQueries({ queryKey: rolePermissionsQueryKey(id) });
        return { data: { id } };
      }
      const created = await createRole({ code: values.code.trim(), name, remark });
      const nextId = created?.data?.id;
      if (nextId) {
        await replaceRolePermissions(nextId, values.permissionIds, readUpdatedAt(created));
      }
      return created;
    },
    listQueryKeyPrefix: ROLE_LIST_QUERY_KEY_PREFIX,
    detailQueryKey: roleDetailQueryKey,
    getEditPath: ROUTES.systemRoleEdit,
    getSuccessMessage: (_result, { isEdit: editing }) => (editing ? '角色與權限已儲存' : '角色已建立'),
    getErrorMessage: (error) => getErrorMessage(error, '儲存失敗'),
    isVersionConflict,
    onVersionConflict: () => {
      detailQuery.refetch();
      rolePermQuery.refetch();
    },
  });

  const canWrite = isEdit ? canUpdate : canCreate;

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(isEdit ? roleEditSchema : roleCreateSchema),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    const permissions = rolePermQuery.data?.data;
    if (!detail || !permissions) {
      return;
    }

    updatedAtRef.current = detail.record?.updatedAt ?? detail.updatedAt ?? null;
    resetValidationSummary();
    reset({
      code: detail.code ?? '',
      name: detail.name ?? '',
      remark: detail.remark ?? '',
      permissionIds: permissions.map((item) => item.id).filter(Boolean),
    });
  }, [detailQuery.data, rolePermQuery.data, reset, resetValidationSummary]);

  const selectedPermissionIds = useWatch({ control, name: 'permissionIds' });

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯角色／權限' : '新增角色';
  const isPageLoading = isEdit && (detailQuery.isLoading || rolePermQuery.isLoading);
  const isDetailNotFound = isEdit && detailQuery.isError;
  const readOnly = !canWrite;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-5xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.systemRoles}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getErrorMessage(detailQuery.error, '找不到角色')}
            </p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={ROLE_FIELD_LABELS}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">基本資料</h2>
                    </div>
                    <div className={cn('grid grid-cols-1 gap-x-4 gap-y-4', '@xl/sheet:grid-cols-2')}>
                      <FloatingInput
                        id="code"
                        type="text"
                        label="代碼"
                        tip="小寫英數與底線，例：material_editor；建立後不可修改"
                        register={register}
                        errors={errors}
                        readOnly={isEdit || readOnly}
                        inputClassName="font-mono"
                      />
                      <FloatingInput
                        id="name"
                        type="text"
                        label="名稱"
                        register={register}
                        errors={errors}
                        readOnly={readOnly}
                      />
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                        readOnly={readOnly}
                        containerClassName="@xl/sheet:col-span-2"
                      />
                    </div>
                  </section>

                  <section>
                    <div className="form-block">
                      <h2 className="form-block__title">角色權限</h2>
                      <p className="form-block__description">
                        依功能勾選可執行的動作；勾建立、更新、審核等會自動勾讀取。儲存時以勾選結果完整取代。
                      </p>
                    </div>
                    {catalogQuery.isLoading ? (
                      <p className="text-sm text-muted-foreground">載入權限目錄…</p>
                    ) : catalogQuery.isError ? (
                      <p className="text-sm text-destructive">
                        {getErrorMessage(catalogQuery.error, '載入權限目錄失敗')}
                      </p>
                    ) : (
                      <RolePermissionEditor
                        catalog={catalog}
                        selectedIds={selectedPermissionIds}
                        onChange={(ids) => setValue('permissionIds', ids, { shouldDirty: true })}
                        disabled={readOnly}
                      />
                    )}
                  </section>
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.systemRoles}>取消</Link>
                  </Button>
                  {canWrite ? (
                    <Button type="submit" disabled={isPending}>
                      {isPending ? '儲存中…' : '儲存'}
                    </Button>
                  ) : null}
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditRolePage;
